// src/components/SEO.tsx
import React from 'react'
import { Helmet } from 'react-helmet-async'

// Default site-wide settings (fallbacks when no props provided)
const siteName = 'Zein Alamah'
const defaultDescription = 'Personal website of Zein Alamah.'
const defaultImage = 'https://zeinalamah.com/favicon.svg'
const defaultUrl = 'https://zeinalamah.com'
const defaultTwitterHandle = '@zein_alamah'

interface SEOProps {
  /**
   * Page title shown in browser tab and search results.
   * @example "Blog Post Title — Zein Alamah"
   */
  title?: string
  /**
   * Meta description for SEO and social previews (recommended 50–160 chars).
   * @example "Explore insights on economics and research by Zein Alamah."
   */
  description?: string
  /**
   * URL of the image for Open Graph/Twitter Card (absolute or root-relative).
   */
  image?: string
  /**
   * Canonical URL of the page to prevent duplicate-content issues.
   */
  url?: string
  /**
   * Open Graph object type: 'website' for general pages, 'article' for blog posts.
   */
  type?: 'website' | 'article'
  /**
   * ISO 8601 date string for articles, e.g. '2025-04-10'.
   */
  publishedTime?: string
}

export const SEO: React.FC<SEOProps> = ({
  title = siteName,
  description = defaultDescription,
  image = defaultImage,
  url = defaultUrl,
  type = 'website',
  publishedTime,
}) => {
  // Conditionally render a canonical link if URL is provided
  const canonicalLink = url ? <link rel="canonical" href={url} /> : null

  return (
    <Helmet>
      {/* -- Standard meta tags -- */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />

      {/* -- Open Graph for rich link previews -- */}
      <meta property="og:type"        content={type} />        {/* website or article */}
      <meta property="og:site_name"   content={siteName} />
      <meta property="og:title"       content={title} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      {url   && <meta property="og:url"   content={url} />}

      {/* -- Twitter Card for Twitter previews -- */}
      <meta name="twitter:card"        content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:site"        content={defaultTwitterHandle} />   {/* your Twitter account */}
      <meta name="twitter:creator"     content={defaultTwitterHandle} />   {/* author account */}
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image"   content={image} />}

      {/* -- Article-specific meta -- */}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}

      {/* -- Canonical link to avoid duplicate content issues -- */}
      {canonicalLink}
    </Helmet>
  )
}
